import classNames from 'classnames';
import React from 'react';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import acceptIcon from '../icons/non-prefilled/12x12/accept.svg';
import archiveIcon from '../icons/non-prefilled/12x12/archive.svg';
import commentIcon from '../icons/non-prefilled/12x12/comment.svg';
import editIcon from '../icons/non-prefilled/12x12/edit.svg';
import mentionIcon from '../icons/non-prefilled/12x12/mention.svg';
import publishIcon from '../icons/non-prefilled/12x12/publish.svg';
import replyIcon from '../icons/non-prefilled/12x12/reply.svg';
import resolveIcon from '../icons/non-prefilled/12x12/resolve.svg';
import shareIcon from '../icons/non-prefilled/12x12/share.svg';
import thankIcon from '../icons/non-prefilled/16x16/thanks.svg';
import verifyIcon from '../icons/non-prefilled/16x16/verified.svg';
import Avatar from './Avatar';
import * as styles from './AvatarWithAction.module.scss';
import type { SVGIconSize } from './SVGIcon';
import SVGIcon from './SVGIcon';

const AVATAR_WITH_ACTION_USER_FRAGMENT = gql(`
  fragment AvatarWithAction_user on User {
    id
    ...Avatar_user
  }
`);

interface ActionProp {
  color: string;
  icon: string | null;
  size: SVGIconSize;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const propsByAction = {
  COMMENT_CREATE: { color: 'comment', icon: commentIcon, size: 'small' },
  COMMENT_MENTION: { color: 'comment', icon: mentionIcon, size: 'small' },
  COMMENT_REPLY: { color: 'comment', icon: replyIcon, size: 'small' },
  COMMENT_RESOLVE: { color: 'comment', icon: resolveIcon, size: 'small' },
  INVITE_ACCEPT: { color: 'success', icon: acceptIcon, size: 'small' },
  POST_ARCHIVE: { color: 'post', icon: archiveIcon, size: 'small' },
  POST_EDIT: { color: 'post', icon: editIcon, size: 'small' },
  POST_PUBLISH: { color: 'post', icon: publishIcon, size: 'small' },
  POST_SHARE: { color: 'post', icon: shareIcon, size: 'small' },
  POST_THANK: { color: 'post', icon: thankIcon, size: 'small' },
  REACTION_CREATE: { color: 'comment', icon: null, size: 'small' },
  VERIFICATION_EXPIRED: { color: 'muted', icon: verifyIcon, size: 'regular' },
  VERIFICATION_REMINDER: {
    color: 'warning',
    icon: verifyIcon,
    size: 'regular',
  },
  VERIFICATION_REQUEST: { color: 'success', icon: verifyIcon, size: 'regular' },
} satisfies Record<string, ActionProp>;

interface AvatarWithActionProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role' | 'className'> {
  action: keyof typeof propsByAction;
  emoji?: string;
  user: DocumentType<typeof AVATAR_WITH_ACTION_USER_FRAGMENT>;
}

const AvatarWithAction = React.forwardRef<
  HTMLDivElement,
  AvatarWithActionProps
>(({ action, emoji, user, ...props }, ref) => {
  const { size, icon, color } = propsByAction[action];

  return (
    <div ref={ref} {...props} className={styles.avatarContainer} role="img">
      <Avatar user={user} />
      <div className={styles.iconContainer}>
        <div
          className={classNames(styles.icon, styles[color])}
          role="presentation"
        >
          <SVGIcon size={size}>{icon || emoji}</SVGIcon>
        </div>
      </div>
    </div>
  );
});

export default AvatarWithAction;
